import React, { useRef } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Slider, 
  Grid, 
  Card, 
  CardContent,
  Button,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Download } from '@mui/icons-material';
import ColorThumbnail from './ColorThumbnail';

const ColorThumbnailControls = ({
  colordata,
  colors,
  // Control states
  thumbnailWidth = 120,
  thumbnailHeight = 90,
  thumbnailView = 'default',
  thumbnailCellRatio = '4:3',
  thumbnailShowGrid = false,
  // Control handlers
  onWidthChange,
  onHeightChange,
  onViewChange,
  onCellRatioChange,
  onShowGridChange,
  // Layout options
  showControls = true,
  layout = 'horizontal', // 'horizontal', 'vertical'
  title,
  showDownload = true, // New prop to control download button visibility
  downloadFilename = 'color-chart-thumbnail', // New prop for custom filename
  settings = {} // NEW: chart settings for orientation
}) => {
  const thumbnailRef = useRef();

  const handleWidthChange = (event, newValue) => {
    if (onWidthChange) onWidthChange(newValue);
  };

  const handleHeightChange = (event, newValue) => {
    if (onHeightChange) onHeightChange(newValue);
  };

  const handleViewChange = (event) => {
    if (onViewChange) onViewChange(event.target.value);
  };

  const handleCellRatioChange = (event) => {
    if (onCellRatioChange) onCellRatioChange(event.target.value);
  };

  // Download functionality - delegates to ColorThumbnail
  const handleDownload = () => {
    if (thumbnailRef.current && thumbnailRef.current.exportAsPng) {
      thumbnailRef.current.exportAsPng(downloadFilename);
    }
  };

  const renderControls = () => (
    <Box sx={{ p: 2 }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Size
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            Width: {thumbnailWidth}px
          </Typography>
          <Slider
            value={thumbnailWidth}
            onChange={handleWidthChange}
            min={60}
            max={300}
            step={10}
            size="small"
          />
          <Typography variant="caption" display="block" gutterBottom sx={{ mt: 1 }}>
            Height: {thumbnailHeight}px
          </Typography>
          <Slider
            value={thumbnailHeight}
            onChange={handleHeightChange}
            min={45}
            max={225}
            step={5}
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                value={thumbnailShowGrid}
                checked={!!thumbnailShowGrid}
                onChange={e => {
                  if (onShowGridChange) onShowGridChange(e.target.checked);
                }}
                size="small"
              />
            }
            label="Show gridlines"
            sx={{ mt: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Style & Ratio
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>View Style</InputLabel>
            <Select
              value={thumbnailView}
              label="View Style"
              onChange={handleViewChange}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="watercolor">Watercolor</MenuItem>
              <MenuItem value="soft">Soft</MenuItem>
              <MenuItem value="minimal">Minimal</MenuItem>
              <MenuItem value="leftToRightGradient">Left→Right Gradient</MenuItem>
              <MenuItem value="sketch">Sketch / Pencil</MenuItem>
              <MenuItem value="rough">Rough Edges</MenuItem>
              <MenuItem value="knitstitch">Knit Stitch</MenuItem>
              <MenuItem value="heart">Heart</MenuItem>
              <MenuItem value="watercolorBleed">Watercolor Bleed</MenuItem>
              <MenuItem value="crayon">Crayon / Colored Pencil</MenuItem>
              <MenuItem value="ink">Ink Outline</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Cell Ratio</InputLabel>
            <Select
              value={thumbnailCellRatio}
              label="Cell Ratio"
              onChange={handleCellRatioChange}
            >
              <MenuItem value="1:1">1:1 (Square)</MenuItem>
              <MenuItem value="3:4">3:4 (Portrait)</MenuItem>
              <MenuItem value="4:3">4:3 (Landscape)</MenuItem>
              <MenuItem value="2:3">2:3 (Tall)</MenuItem>
              <MenuItem value="3:2">3:2 (Wide)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderThumbnail = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center',
      p: 2
    }}>
      <Box sx={{ mb: showDownload ? 2 : 0 }}>
        <ColorThumbnail
          ref={thumbnailRef}
          colordata={colordata}
          colors={colors}
          width={thumbnailWidth}
          height={thumbnailHeight}
          view={thumbnailView}
          cellRatio={thumbnailCellRatio}
          showGrid={thumbnailShowGrid}
          settings={settings}
        />
      </Box>
      {showDownload && showControls && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{ fontSize: '0.75rem' }}
          >
            Download
          </Button>
        </Box>
      )}
    </Box>
  );

  if (!showControls) {
    return renderThumbnail();
  }

  if (layout === 'vertical') {
    return (
      <Card>
        <CardContent>
          {renderControls()}
          {renderThumbnail()}
        </CardContent>
      </Card>
    );
  }

  // Horizontal layout (default)
  return (
    <Card>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderControls()}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderThumbnail()}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ColorThumbnailControls;
